import { useCallback, useRef, useState } from 'react'
import { useAnalytics } from '@/hooks/useAnalytics'
import {
  type ApiPost,
  createPost,
  deletePost,
  getCommunityPosts,
  pinPost as pinPostApi,
  searchCommunityPosts,
  toggleLike,
} from '@/lib/api'

interface PendingPost extends ApiPost {
  isPending: true
}

type FeedPost = ApiPost | PendingPost

export function useCommunityFeed() {
  const { capture } = useAnalytics()
  const [posts, setPosts] = useState<FeedPost[]>([])
  const [, setCursor] = useState<{ before_created_at: string; before_id: string } | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Ref mirrors so loadMore can read latest values without recreating on every render.
  const cursorRef = useRef<{ before_created_at: string; before_id: string } | null>(null)
  const hasMoreRef = useRef(true)
  const loadingRef = useRef(false)
  const searchQueryRef = useRef('')

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return
    loadingRef.current = true
    setLoading(true)
    setError(null)
    try {
      const result = searchQueryRef.current
        ? await searchCommunityPosts(searchQueryRef.current, cursorRef.current ?? undefined)
        : await getCommunityPosts(cursorRef.current ?? undefined)
      setPosts((prev) => [...prev, ...result.posts])
      cursorRef.current = result.next_cursor
      setCursor(result.next_cursor)
      hasMoreRef.current = result.next_cursor !== null
      setHasMore(result.next_cursor !== null)
    } catch {
      // Stop auto-pagination loops after a failed request (e.g. 403).
      hasMoreRef.current = false
      setHasMore(false)
      setError('load')
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [])

  async function submitSearch(query: string) {
    if (loadingRef.current) return
    const trimmedQuery = query.trim()
    capture('community_feed_search_submitted', { query_length: trimmedQuery.length })
    searchQueryRef.current = trimmedQuery
    setSearchQuery(trimmedQuery)
    setPosts([])
    cursorRef.current = null
    setCursor(null)
    hasMoreRef.current = true
    setHasMore(true)
    setError(null)
    await loadMore()
  }

  async function clearSearch() {
    if (loadingRef.current) return
    capture('community_feed_search_cleared')
    searchQueryRef.current = ''
    setSearchQuery('')
    setPosts([])
    cursorRef.current = null
    setCursor(null)
    hasMoreRef.current = true
    setHasMore(true)
    setError(null)
    await loadMore()
  }

  async function submitPost(body: string) {
    capture('community_post_create_submitted', { body_length: body.trim().length })
    const tempId = `pending-${crypto.randomUUID()}`
    const tempPost: PendingPost = {
      id: tempId,
      user_id: '',
      author_name: '',
      body,
      created_at: new Date().toISOString(),
      isPending: true,
      is_admin: false,
      is_pinned: false,
      like_count: 0,
      liked_by_me: false,
      comment_count: 0,
    }
    setPosts((prev) => [tempPost, ...prev])
    try {
      const created = await createPost(body)
      setPosts((prev) => prev.map((p) => (p.id === tempId ? created : p)))
      capture('community_post_created', { post_id: created.id })
    } catch {
      setPosts((prev) => prev.filter((p) => p.id !== tempId))
      setError('post')
      capture('community_post_create_failed')
    }
  }

  async function removePost(id: string) {
    capture('community_post_deleted', { post_id: id })
    setPosts((prev) => prev.filter((p) => p.id !== id))
    try {
      await deletePost(id)
    } catch {
      // Silently fail — post is already removed from UI
    }
  }

  async function likePost(id: string) {
    let wasLiked = false
    setPosts((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p

        wasLiked = p.liked_by_me
        return {
          ...p,
          liked_by_me: !p.liked_by_me,
          like_count: p.liked_by_me ? p.like_count - 1 : p.like_count + 1,
        }
      })
    )
    capture(wasLiked ? 'community_post_unliked' : 'community_post_liked', { post_id: id })
    try {
      await toggleLike(id)
    } catch {
      // Rollback: toggle again
      setPosts((prev) =>
        prev.map((p) =>
          p.id === id
            ? {
                ...p,
                liked_by_me: !p.liked_by_me,
                like_count: p.liked_by_me ? p.like_count - 1 : p.like_count + 1,
              }
            : p
        )
      )
    }
  }

  async function pinPostAction(id: string, pinned: boolean) {
    setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, is_pinned: pinned } : p)))
    try {
      await pinPostApi(id, pinned)
    } catch {
      // Rollback
      setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, is_pinned: !pinned } : p)))
    }
  }

  return {
    posts,
    hasMore,
    loading,
    error,
    searchQuery,
    isSearchActive: searchQuery.length > 0,
    loadMore,
    submitSearch,
    clearSearch,
    submitPost,
    removePost,
    likePost,
    pinPost: pinPostAction,
  }
}
